import { useEffect, useLayoutEffect, useState } from 'react'
import { captureException } from '@sentry/react'
import { useAppDispatch } from '../../../../../../redux/hooks/useAppDispatch'
import {
  Button,
  ButtonGroup,
  CheckIcon,
  CloseIcon,
  cn,
  DateInput,
  ErrorMessage,
  Field,
  FormLabel,
  Heading,
  Input,
  Paragraph,
  Popup,
  Select,
  showToast,
  Textarea,
} from '@quickmd-llc/design-system'
import {
  ADD_TEXT,
  ADDITIONAL_INFORMATION_TEXT,
  CANCEL_TEXT,
  CREDIT_EXPIRATION_DATE_TEXT,
  CREDITS_UPDATED_TEXT,
  CURRENT_BALANCE_TEXT,
  DEDUCT_TEXT,
  EDIT_GENERAL_CREDITS_TEXT,
  EDIT_REFERRAL_CREDITS_TEXT,
  ENTER_AMOUNT_TEXT,
  NEW_BALANCE_TEXT,
  REASON_FOR_GENERAL_CREDITS_TEXT,
  REASON_FOR_REFERRAL_CREDITS_TEXT,
  REFERRAL_CREDITS_NOTE_TEXT,
  SELECT_REASON_TEXT,
  UPDATE_CREDITS_TEXT,
} from '../../../../../../utils/constants'
import { usePutPatientsPatientIdCredit } from '../../../../../../api/generated/patient/patient'
import { useGetLookups } from '../../../../../../api/generated/lookup/lookup'
import {
  type CreditReasonLookupItemOutContract,
  LookupType,
} from '../../../../../../api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { getPatientInfo, getPatientAuditLogs } from '../../../../../../store/charts/ChartsThunk'
import { dateHelper, DEFAULT_DATE_FORMAT_WITHOUT_TIME } from '../../../../../../utils/dateUtils'

type CreditType = 'standard' | 'referral'

type Props = {
  isOpen: boolean
  creditType: CreditType
  currentBalance: number
  patientId: string
  onClose: () => void
}

const onlyNumbersRegex = /^\d*\.?\d{0,2}$/

export const EditCreditModal = ({ isOpen, creditType, currentBalance, patientId, onClose }: Props) => {
  const dispatch = useAppDispatch()

  const isGeneral = creditType === 'standard'
  const title = isGeneral ? EDIT_GENERAL_CREDITS_TEXT : EDIT_REFERRAL_CREDITS_TEXT
  const reasonLabel = isGeneral ? REASON_FOR_GENERAL_CREDITS_TEXT : REASON_FOR_REFERRAL_CREDITS_TEXT

  const [mode, setMode] = useState<string>(ADD_TEXT)
  const [amount, setAmount] = useState('')
  const [selectedReason, setSelectedReason] = useState('')
  const [expirationDate, setExpirationDate] = useState('')
  const [additionalInfo, setAdditionalInfo] = useState('')
  const [amountError, setAmountError] = useState('')

  const lookupType = isGeneral ? LookupType.standardcreditreasons : LookupType.referralcreditreasons
  const { data: reasons = [] } = useGetLookups<CreditReasonLookupItemOutContract[]>({ type: lookupType })

  const trimmedAmount = amount.trim()
  const parsedAmount = parseFloat(trimmedAmount)
  const parsedAmountFloat = parsedAmount || 0

  const amountExceedsBalance = mode === DEDUCT_TEXT && parsedAmountFloat > 0 && parsedAmountFloat > currentBalance

  const canSubmit = parsedAmountFloat > 0 && !!selectedReason && !amountExceedsBalance

  const {
    mutate: updateCredit,
    isPending,
    isSuccess,
    reset,
  } = usePutPatientsPatientIdCredit({
    mutation: {
      onSuccess: () => {
        // @ts-expect-error TS2554 -- MIGRATION: not approved, fix when encountered
        const patientInfoAction = getPatientInfo(patientId)
        // @ts-expect-error TS2554 -- MIGRATION: not approved, fix when encountered
        const auditLogsAction = getPatientAuditLogs(patientId)
        if (patientInfoAction) dispatch(patientInfoAction)
        if (auditLogsAction) dispatch(auditLogsAction)
      },
      onError: (error: unknown) => {
        captureException(error)
        showToast({
          type: 'error',
          message: 'Failed to update credits. Please try again.',
          position: 'bottom-right',
        })
      },
    },
  })

  // DS Popup renders into a portal outside .tailwind-scoped — add the class early (useLayoutEffect
  // runs before child useEffect so the Input's left-padding calculation sees it)
  useLayoutEffect(() => {
    const portalRoot = document.getElementById('headlessui-portal-root')
    if (portalRoot) portalRoot.classList.add('tailwind-scoped')
  }, [])

  // DS Popup is always mounted; reset form fields each time the modal opens so the
  // user gets a clean slate for general vs referral (creditType also changes between opens).
  useEffect(() => {
    if (isOpen) {
      /* eslint-disable react-hooks/set-state-in-effect -- DS Popup never unmounts so this
         effect is the only way to reset prior form state when the modal reopens. */
      setMode(ADD_TEXT)
      setAmount('')
      setSelectedReason('')
      setExpirationDate('')
      setAdditionalInfo('')
      setAmountError('')
      /* eslint-enable react-hooks/set-state-in-effect */
      reset()
    }
  }, [isOpen, creditType, reset])

  // String primitive dep so a React Query background refetch returning identical data
  // doesn't reset a user-edited expiration date.
  const selectedReasonExpiry = reasons.find((r) => r.Id === selectedReason)?.CreditExpiresAtUtc ?? null

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect -- expiration auto-populates from a
       server-side preset (CreditExpiresAtUtc); this is external-system synchronization. */
    if (!selectedReason) {
      setExpirationDate('')
      return
    }
    /* eslint-enable react-hooks/set-state-in-effect */
    if (selectedReasonExpiry) {
      // CreditExpiresAtUtc is midnight UTC — extract the date portion directly
      // rather than converting to local time, since the server's intent is that calendar date.
      setExpirationDate(selectedReasonExpiry.split('T')[0] ?? '')
    } else {
      const d = new Date()
      d.setFullYear(d.getFullYear() + 1)
      setExpirationDate(dateHelper.formatDate(d, DEFAULT_DATE_FORMAT_WITHOUT_TIME) ?? '')
    }
  }, [selectedReason, selectedReasonExpiry])

  // Do NOT call reset() on close here — that flips isSuccess→false mid-animation (visible flash).
  // The reset-on-reopen effect above handles cleanup when the modal next opens.
  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => onClose(), 2000)
      return () => clearTimeout(timer)
    }
  }, [isSuccess, onClose])

  const newBalance =
    parsedAmountFloat > 0 ?
      parseFloat((currentBalance + (mode === ADD_TEXT ? parsedAmountFloat : -parsedAmountFloat)).toFixed(2))
    : null

  const newBalanceDisplay =
    newBalance !== null ?
      `${NEW_BALANCE_TEXT}: ${newBalance < 0 ? `-$${Math.abs(newBalance).toFixed(2)}` : `$${newBalance.toFixed(2)}`}`
    : `${NEW_BALANCE_TEXT}: $--`

  const validate = (): boolean => {
    if (!trimmedAmount || isNaN(parsedAmount) || parsedAmount <= 0) {
      setAmountError('Amount is required and must be greater than 0')
      return false
    }
    if (parsedAmount > 9999) {
      setAmountError('Amount cannot exceed $9,999')
      return false
    }
    setAmountError('')
    return true
  }

  const handleSubmit = () => {
    if (!validate()) return
    const creditAmountChange = mode === DEDUCT_TEXT ? -parsedAmount : parsedAmount
    updateCredit({
      patientId,
      data: {
        Type: creditType,
        CreditAmountChange: creditAmountChange,
        ReasonGlobalKey: selectedReason,
        Comment: additionalInfo.trim() || null,
        ExpiresAtDateOverride:
          mode === ADD_TEXT && expirationDate ? new Date(expirationDate + 'T00:00:00.000Z').toISOString() : null,
      },
    })
  }

  const handleClose = () => {
    if (isPending) return
    if (!isSuccess) reset()
    onClose()
  }

  return (
    <Popup
      isOpen={isOpen}
      showCloseIcon
      header={
        isSuccess ? undefined : (
          {
            title,
            subtitle: isGeneral ? undefined : REFERRAL_CREDITS_NOTE_TEXT,
            closeIcon: <CloseIcon iconSize='m' className='text-text-dark' />,
          }
        )
      }
      body={
        isSuccess ?
          <div className='grid justify-center gap-sp-4'>
            <div className='flex h-[96px] w-[96px] items-center justify-center justify-self-center rounded-full bg-primary-seafoam'>
              <CheckIcon iconSize='xxl' className='justify-center text-text-inverse' />
            </div>
            <Heading level='h1'>{CREDITS_UPDATED_TEXT}</Heading>
          </div>
        : <div className='flex flex-col gap-sp-3'>
            <ButtonGroup
              type='connected'
              priority='secondary'
              selectedIndex={mode === ADD_TEXT ? 0 : 1}
              onButtonSelection={(index: number) => {
                setMode(index === 0 ? ADD_TEXT : DEDUCT_TEXT)
                setAmountError('')
              }}>
              <Button size='s' priority={mode === ADD_TEXT ? 'primary' : 'secondary'}>
                {mode === ADD_TEXT && <CheckIcon iconSize='s' className='mr-sp-1' />}
                {ADD_TEXT}
              </Button>
              <Button size='s' priority={mode === DEDUCT_TEXT ? 'primary' : 'secondary'}>
                {mode === DEDUCT_TEXT && <CheckIcon iconSize='s' className='mr-sp-1' />}
                {DEDUCT_TEXT}
              </Button>
            </ButtonGroup>

            <Field>
              <div className='mb-1 flex items-end justify-between'>
                <FormLabel className='mb-0'>{ENTER_AMOUNT_TEXT}</FormLabel>
                <Paragraph size='md' className='text-text-light'>
                  {CURRENT_BALANCE_TEXT}: ${parseFloat(String(currentBalance)).toFixed(2)}
                </Paragraph>
              </div>
              <div className='flex gap-sp-2'>
                <div className='flex-1'>
                  <Input
                    placeholder='Amount'
                    value={amount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (e.target.value.match(onlyNumbersRegex) && e.target.value !== '.') {
                        setAmount(e.target.value)
                        setAmountError('')
                      }
                    }}
                    error={!!amountError || amountExceedsBalance}
                  />
                  {amountError || amountExceedsBalance ?
                    <ErrorMessage>{amountError || 'Amount cannot exceed current balance'}</ErrorMessage>
                  : null}
                </div>
                <div
                  className={cn(
                    'flex h-12 w-[210px] min-w-0 flex-shrink-0 items-center overflow-hidden rounded-md border px-sp-2',
                    newBalance === null ? 'border-border-medium bg-surface-disabled'
                    : newBalance < 0 ? 'border-error bg-surface-error'
                    : 'border-border-highlight bg-surface-active',
                  )}
                  title={newBalanceDisplay}>
                  <Paragraph
                    size='md'
                    className={cn(
                      'min-w-0 truncate',
                      newBalance === null ? 'text-text-light'
                      : newBalance < 0 ? 'text-text-error'
                      : 'text-text-highlight',
                    )}>
                    {newBalanceDisplay}
                  </Paragraph>
                </div>
              </div>
            </Field>

            <Field>
              <FormLabel>{reasonLabel}</FormLabel>
              <Select
                value={selectedReason}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedReason(e.target.value)}>
                <option value=''>{SELECT_REASON_TEXT}</option>
                {reasons.map((r) => (
                  <option key={r.Id ?? ''} value={r.Id ?? ''}>
                    {r.Text}
                  </option>
                ))}
              </Select>
            </Field>

            {mode === ADD_TEXT && selectedReason ?
              <Field>
                <FormLabel>{CREDIT_EXPIRATION_DATE_TEXT}</FormLabel>
                <DateInput
                  value={expirationDate}
                  onChange={setExpirationDate}
                  min={dateHelper.formatDate(new Date(), DEFAULT_DATE_FORMAT_WITHOUT_TIME) ?? ''}
                />
              </Field>
            : null}

            <Field>
              <FormLabel>{ADDITIONAL_INFORMATION_TEXT}</FormLabel>
              <Textarea
                className='resize-y'
                placeholder={isGeneral ? 'Type something...' : 'Type your message...'}
                value={additionalInfo}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAdditionalInfo(e.target.value)}
              />
            </Field>
          </div>
      }
      footer={
        !isSuccess ?
          <div className='flex justify-end gap-sp-1'>
            <Button priority='secondary' onClick={handleClose} disabled={isPending} size='m'>
              {CANCEL_TEXT}
            </Button>
            <Button onClick={handleSubmit} loading={isPending} disabled={!canSubmit || isPending} size='m'>
              {UPDATE_CREDITS_TEXT}
            </Button>
          </div>
        : null
      }
      onClose={handleClose}
      styles={{ containerClassName: 'w-[512px]' }}
    />
  )
}
